'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { MapPin, Phone, Star, CheckCircle2, X, ChevronDown, Shield, CreditCard, Zap, Mountain, Users, Clock, ArrowRight, Lock, AlertCircle, Loader2, MessageCircle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Vehicle {
  id: string;
  name: string;
  model: string;
  capacity: number;
  icon: string;
  badge?: string;
  fares: Record<string, number>;
}

interface BookingFormData {
  name: string;
  mobile: string;
  email: string;
  pickupCity: string;
  travelDate: string;
  travelTime: string;
  tripType: 'one-way' | 'round-trip';
  passengers: number;
  specialRequests: string;
}

type PaymentOption = '20percent' | '100percent';
type PaymentStatus = 'idle' | 'creating-order' | 'opening-checkout' | 'verifying' | 'confirmed' | 'failed';

// ─── Data ─────────────────────────────────────────────────────────────────────

const PICKUP_CITIES = [
  { id: 'pathankot', label: 'Pathankot', distance: '~220 km' },
  { id: 'amritsar', label: 'Amritsar', distance: '~280 km' },
  { id: 'chandigarh', label: 'Chandigarh', distance: '~380 km' },
  { id: 'delhi', label: 'Delhi NCR', distance: '~580 km' },
];

const VEHICLES: Vehicle[] = [
  {
    id: 'hatchback',
    name: 'Hatchback',
    model: 'Swift Dzire / WagonR',
    capacity: 4,
    icon: '🚗',
    badge: 'Best Value',
    fares: { pathankot: 7499, amritsar: 9499, chandigarh: 12999, delhi: 18999 },
  },
  {
    id: 'sedan',
    name: 'Sedan',
    model: 'Honda City / Ciaz',
    capacity: 4,
    icon: '🚙',
    fares: { pathankot: 8999, amritsar: 10999, chandigarh: 14999, delhi: 21999 },
  },
  {
    id: 'suv',
    name: 'SUV / Ertiga',
    model: 'Ertiga / Rumion / Scorpio',
    capacity: 6,
    icon: '🚐',
    badge: 'Hill Expert',
    fares: { pathankot: 11999, amritsar: 13999, chandigarh: 18999, delhi: 26999 },
  },
  {
    id: 'innova',
    name: 'Innova',
    model: 'Toyota Innova / Crysta',
    capacity: 7,
    icon: '🚌',
    badge: 'Best Seller',
    fares: { pathankot: 13999, amritsar: 16999, chandigarh: 22999, delhi: 31999 },
  },
];

const INCLUSIONS = [
  'AC Vehicle', 'Professional Hill Driver', 'Fuel Charges',
  'Toll Tax', 'State Tax', 'Driver Allowance',
];

const EXCLUSIONS = [
  'Parking Charges (Extra)', 'Personal Expenses', 'Pony / Porter Charges',
  'Entry Tickets', 'Meals & Accommodation', 'Travel Insurance',
];

const FAQS = [
  { q: 'Is parking included in the fare?', a: 'No. Parking charges are always extra and paid directly by the customer wherever applicable, unless specifically mentioned in your booking confirmation.' },
  { q: 'Will AC work on mountain roads?', a: 'AC is available during normal driving. On steep hill climbs and high-altitude roads, the driver may temporarily switch off AC to prevent engine overheating and ensure safe vehicle operation. AC is switched back on once normal road conditions resume.' },
  { q: 'Can I book a round trip?', a: 'Yes. Select "Round Trip" and the fare will update automatically. Round trip fares include return journey charges.' },
  { q: 'What is the best time for Manimahesh Yatra?', a: 'The official Yatra season is August–September. The lake is accessible from July to October. Radha Ashtami (August/September) is the most auspicious time.' },
  { q: 'Is the trek included?', a: 'The taxi drops you at Hadsar base camp. The 13 km trek to Manimahesh Lake is on foot. Pony and porter services are available at Hadsar.' },
  { q: 'How do I pay?', a: 'You can pay 20% advance online to confirm your booking and pay the balance before departure, or pay 100% online for a hassle-free experience.' },
];

function generateBookingId(): string {
  const prefix = 'GJT';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

// Load Razorpay script dynamically and resolve when ready
function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).Razorpay) {
      resolve();
      return;
    }
    const existing = document.getElementById('razorpay-checkout-js');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay script')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script. Please check your internet connection.'));
    document.head.appendChild(script);
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ManimaheshYatraClient() {
  const router = useRouter();
  const { user } = useAuth();

  const [selectedPickup, setSelectedPickup] = useState('pathankot');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle>(VEHICLES[0]);
  const [tripType, setTripType] = useState<'one-way' | 'round-trip'>('one-way');

  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    mobile: '',
    email: '',
    pickupCity: 'Pathankot',
    travelDate: '',
    travelTime: '',
    tripType: 'one-way',
    passengers: 2,
    specialRequests: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});

  const [paymentOption, setPaymentOption] = useState<PaymentOption>('20percent');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // ─── Fare Calculation ───────────────────────────────────────────────────────

  const baseFare = selectedVehicle.fares[selectedPickup] ?? 0;
  const roundTripFare = tripType === 'round-trip' ? Math.round(baseFare * 1.8) : baseFare;
  const advanceAmount = Math.ceil(roundTripFare * 0.2);
  const payableNow = paymentOption === '20percent' ? advanceAmount : roundTripFare;
  const pendingAmount = paymentOption === '20percent' ? roundTripFare - advanceAmount : 0;

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleVehicleSelect = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
  }, []);

  const handlePickupChange = useCallback((city: string) => {
    setSelectedPickup(city);
  }, []);

  const handleTripTypeChange = useCallback((type: 'one-way' | 'round-trip') => {
    setTripType(type);
  }, []);

  const handleBookNow = () => {
    setFormData((prev) => ({
      ...prev,
      pickupCity: PICKUP_CITIES.find((c) => c.id === selectedPickup)?.label ?? 'Pathankot',
      tripType,
    }));
    setShowBookingForm(true);
    setTimeout(() => {
      document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof BookingFormData, string>> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.mobile.trim() || !/^[6-9]\d{9}$/.test(formData.mobile)) errors.mobile = 'Valid 10-digit mobile required';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Valid email required';
    if (!formData.travelDate) errors.travelDate = 'Travel date is required';
    if (!formData.travelTime) errors.travelTime = 'Travel time is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setPaymentStatus('creating-order');
    setPaymentError(null);

    try {
      // Step 1: Load Razorpay script dynamically
      await loadRazorpayScript();

      const bookingId = generateBookingId();

      // Step 2: Create Razorpay order via server
      const orderRes = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: payableNow, currency: 'INR', bookingId }),
      });

      const orderData = await orderRes.json();

      if (!orderData.success || !orderData.orderId) {
        throw new Error(orderData.error || 'Failed to create payment order');
      }

      setPaymentStatus('opening-checkout');

      // Step 3: Open Razorpay checkout
      await new Promise<void>((resolve, reject) => {
        const options = {
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'Ghumo Ji Travels',
          description: `Manimahesh Yatra – ${formData.pickupCity} → Manimahesh Sahib`,
          image: '/assets/images/Screenshot_20260806-205147_WhatsApp_Business-1786029755512.png',
          order_id: orderData.orderId,
          prefill: {
            name: formData.name,
            email: formData.email,
            contact: `91${formData.mobile}`,
          },
          notes: {
            bookingId,
            pickup: formData.pickupCity,
            drop: 'Manimahesh Sahib',
            vehicle: selectedVehicle.name,
            paymentType: paymentOption === '20percent' ? '20% Advance' : '100% Full Payment',
          },
          theme: { color: '#0F4C81' },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
            setPaymentStatus('verifying');
            try {
              // Step 4: Verify payment signature server-side
              const verifyRes = await fetch('/api/razorpay/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });
              const verifyData = await verifyRes.json();
              if (!verifyData.success) throw new Error('Payment verification failed');

              setPaymentStatus('confirmed');

              // Step 5: Save booking to Supabase
              await fetch('/api/booking/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  booking_id: bookingId,
                  user_id: user ? (user as { id: string }).id : null,
                  customer_name: formData.name,
                  customer_mobile: formData.mobile,
                  customer_email: formData.email,
                  pickup: formData.pickupCity,
                  drop_location: 'Manimahesh Sahib (via Bharmour)',
                  travel_date: formData.travelDate,
                  travel_time: formData.travelTime,
                  vehicle: selectedVehicle.name,
                  vehicle_model: selectedVehicle.model,
                  passengers: formData.passengers,
                  trip_type: tripType,
                  total_amount: roundTripFare,
                  amount_paid: payableNow,
                  pending_amount: pendingAmount,
                  coupon_code: null,
                  coupon_discount: 0,
                  booking_status: 'confirmed',
                  payment_status: paymentOption === '100percent' ? 'fully_paid' : 'advance_paid',
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  special_requests: formData.specialRequests,
                }),
              });

              resolve();
              router.push(`/booking-confirmation?id=${bookingId}`);
            } catch (verifyError) {
              reject(verifyError);
            }
          },
          modal: {
            ondismiss: () => {
              setPaymentStatus('idle');
              setIsSubmitting(false);
              resolve();
            },
          },
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rzp = new (window as any).Razorpay(options);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rzp.on('payment.failed', (res: any) => {
          setPaymentStatus('failed');
          setIsSubmitting(false);
          const errMsg = res.error?.description || 'Payment failed';
          setPaymentError(errMsg);
          reject(new Error(errMsg));
        });
        rzp.open();
      });

    } catch (error) {
      setIsSubmitting(false);
      setPaymentStatus('failed');
      const errMsg = error instanceof Error ? error.message : 'Payment setup failed';
      setPaymentError(errMsg);
    }
  };

  const paymentStatusMessages: Record<PaymentStatus, { icon: string; text: string; color: string } | null> = {
    idle: null,
    'creating-order': { icon: '🔒', text: 'Initializing secure payment...', color: 'text-blue-700' },
    'opening-checkout': { icon: '🔄', text: 'Opening Razorpay checkout...', color: 'text-blue-700' },
    verifying: { icon: '🔐', text: 'Verifying payment signature...', color: 'text-amber-700' },
    confirmed: { icon: '✅', text: 'Payment confirmed!', color: 'text-green-700' },
    failed: { icon: '❌', text: 'Payment failed. Please try again.', color: 'text-red-700' },
  };

  const currentStatus = paymentStatusMessages[paymentStatus];

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_13dcb0353-1786026260776.png"
          alt="Shri Manimahesh Kailash peak and Dal Lake sacred pilgrimage site in Chamba Himachal Pradesh"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 lg:px-8 pb-10 lg:pb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-[#FFB703] text-[#0F4C81] text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                🛕 Sacred Pilgrimage
              </span>
              <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
                Yatra Season: Aug – Sep
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-3">
              Manimahesh Yatra<br />
              <span className="text-[#FFB703]">Taxi Package</span>
            </h1>
            <p className="text-white/85 text-base lg:text-lg mb-5 leading-relaxed">
              Pathankot → Bharmour → Manimahesh Sahib &amp; Dal Lake<br />
              <span className="text-[#FFB703] font-semibold">Hatchback from ₹7,499</span> • AC Vehicle • Experienced Hill Drivers
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleBookNow}
                className="bg-[#FFB703] hover:bg-yellow-400 text-[#0F4C81] font-extrabold px-6 py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 text-sm"
              >
                <Zap size={16} />
                Book Now – Instant Confirmation
              </button>
              <a
                href="https://wa.me/918894915859?text=Hi%2C%20I%20want%20to%20book%20Manimahesh%20Yatra%20taxi.%20Please%20share%20details."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] hover:bg-green-500 text-white font-bold px-5 py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 text-sm"
              >
                <MessageCircle size={16} />
                WhatsApp Enquiry
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick Stats ──────────────────────────────────────────────────── */}
      <section className="bg-[#0F4C81] text-white py-4">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            {[
              { icon: <Star size={18} className="text-[#FFB703]" />, label: '4.9★ Rating', sub: '300+ Reviews' },
              { icon: <Shield size={18} className="text-[#FFB703]" />, label: 'Verified Drivers', sub: 'Hill Experts' },
              { icon: <Clock size={18} className="text-[#FFB703]" />, label: '24×7 Support', sub: 'Always Available' },
              { icon: <CheckCircle2 size={18} className="text-[#FFB703]" />, label: 'Instant Booking', sub: 'Confirmed in 2 min' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-1 py-2">
                {item.icon}
                <span className="font-bold text-sm">{item.label}</span>
                <span className="text-blue-300 text-xs">{item.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Booking Section ─────────────────────────────────────────── */}
      <section className="py-10 lg:py-14 bg-gray-50">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Left: Vehicle Selection */}
            <div className="lg:col-span-2 space-y-6">

              {/* Step 1: Pickup City */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-base font-extrabold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#0F4C81] text-white text-xs font-bold flex items-center justify-center">1</span>
                  Select Pickup City
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PICKUP_CITIES.map((city) => (
                    <button
                      key={city.id}
                      onClick={() => handlePickupChange(city.id)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-200 text-sm font-semibold ${
                        selectedPickup === city.id
                          ? 'border-[#0F4C81] bg-blue-50 text-[#0F4C81] shadow-md'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      <MapPin size={15} className={selectedPickup === city.id ? 'text-[#0F4C81]' : 'text-gray-400'} />
                      <span>{city.label}</span>
                      <span className={`text-xs font-normal ${selectedPickup === city.id ? 'text-blue-500' : 'text-gray-400'}`}>{city.distance}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Trip Type */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-base font-extrabold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#0F4C81] text-white text-xs font-bold flex items-center justify-center">2</span>
                  Trip Type
                </h2>
                <div className="flex gap-3">
                  {(['one-way', 'round-trip'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleTripTypeChange(type)}
                      className={`flex-1 py-3 rounded-xl border-2 font-bold text-sm transition-all duration-200 ${
                        tripType === type
                          ? 'border-[#0F4C81] bg-[#0F4C81] text-white shadow-md'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      {type === 'one-way' ? '→ One Way' : '⇄ Round Trip'}
                    </button>
                  ))}
                </div>
                {tripType === 'round-trip' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                    ℹ️ Round trip fare includes return journey. Driver stays with you throughout.
                  </p>
                )}
              </div>

              {/* Step 3: Vehicle Selection */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-base font-extrabold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#0F4C81] text-white text-xs font-bold flex items-center justify-center">3</span>
                  Select Vehicle
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {VEHICLES.map((vehicle) => {
                    const fare = vehicle.fares[selectedPickup] ?? 0;
                    const displayFare = tripType === 'round-trip' ? Math.round(fare * 1.8) : fare;
                    const isSelected = selectedVehicle.id === vehicle.id;
                    return (
                      <button
                        key={vehicle.id}
                        onClick={() => handleVehicleSelect(vehicle)}
                        className={`relative flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 w-full ${
                          isSelected
                            ? 'border-[#0F4C81] bg-blue-50 shadow-md ring-2 ring-[#0F4C81]/20'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#0F4C81] flex items-center justify-center">
                            <CheckCircle2 size={12} className="text-white" />
                          </div>
                        )}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${isSelected ? 'bg-[#0F4C81]/10' : 'bg-gray-100'}`}>
                          {vehicle.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-extrabold text-sm ${isSelected ? 'text-[#0F4C81]' : 'text-gray-800'}`}>{vehicle.name}</span>
                            {vehicle.badge && (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${vehicle.badge === 'Best Value' ? 'bg-[#FFB703] text-[#0F4C81]' : 'bg-blue-100 text-blue-700'}`}>
                                {vehicle.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{vehicle.model}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Users size={11} className="text-gray-400" />
                            <span className="text-xs text-gray-400">Up to {vehicle.capacity} pax</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-extrabold ${isSelected ? 'text-[#0F4C81]' : 'text-gray-800'}`}>₹{displayFare.toLocaleString('en-IN')}</p>
                          <p className="text-xs text-gray-400">{tripType === 'one-way' ? 'One Way' : 'Round Trip'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Inclusions / Exclusions */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
                  <h3 className="font-extrabold text-green-800 text-sm mb-3 flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-green-600" />
                    Inclusions
                  </h3>
                  <ul className="space-y-1.5">
                    {INCLUSIONS.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-green-700">
                        <span className="text-green-500 font-bold">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                  <h3 className="font-extrabold text-red-800 text-sm mb-3 flex items-center gap-2">
                    <X size={16} className="text-red-500" />
                    Exclusions
                  </h3>
                  <ul className="space-y-1.5">
                    {EXCLUSIONS.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-red-700">
                        <span className="text-red-400 font-bold">✗</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-red-600 mt-3 font-semibold bg-red-100 rounded-lg px-2 py-1.5">
                    ⚠️ Parking charges are always extra and paid directly by the customer.
                  </p>
                </div>
              </div>

              {/* Route Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-extrabold text-gray-800 text-sm mb-4 flex items-center gap-2">
                  <Mountain size={16} className="text-[#0F4C81]" />
                  Route &amp; Journey Details
                </h3>
                <div className="space-y-3">
                  {[
                    { from: PICKUP_CITIES.find((c) => c.id === selectedPickup)?.label ?? 'Pathankot', to: 'Bharmour', note: 'Via Dalhousie & Chamba', time: '7–8 hrs' },
                    { from: 'Bharmour', to: 'Hadsar Base Camp', note: 'Drive to trek starting point', time: '45 min' },
                    { from: 'Hadsar', to: 'Manimahesh Lake & Kailash Peak', note: '13 km trek (pony/porter available)', time: '5–6 hrs trek' },
                  ].map((leg, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="flex flex-col items-center mt-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#00A86B] flex-shrink-0" />
                        {i < 2 && <div className="w-0.5 h-8 bg-gray-200 my-1" />}
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-gray-800">{leg.from}</span>
                          <ArrowRight size={12} className="text-gray-400" />
                          <span className="font-bold text-sm text-[#0F4C81]">{leg.to}</span>
                        </div>
                        <p className="text-xs text-gray-500">{leg.note} • ~{leg.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-extrabold text-gray-800 text-sm mb-4">Frequently Asked Questions</h3>
                <div className="space-y-2">
                  {FAQS.map((faq, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-sm text-gray-800 pr-4">{faq.q}</span>
                        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                      </button>
                      {openFaq === i && (
                        <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Booking Summary + Sticky CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#0F4C81] to-[#1a6bb5] p-4 text-white">
                    <h3 className="font-extrabold text-base">Booking Summary</h3>
                    <p className="text-blue-200 text-xs mt-0.5">Review your selection</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Vehicle</span>
                      <span className="font-bold text-gray-800">{selectedVehicle.name}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Pickup</span>
                      <span className="font-bold text-gray-800">{PICKUP_CITIES.find((c) => c.id === selectedPickup)?.label}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Destination</span>
                      <span className="font-bold text-gray-800">Manimahesh Sahib</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Trip Type</span>
                      <span className="font-bold text-gray-800 capitalize">{tripType === 'one-way' ? 'One Way' : 'Round Trip'}</span>
                    </div>
                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-500">Base Fare</span>
                        <span className="font-semibold text-gray-700">₹{roundTripFare.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-400 mb-1">
                        <span>Toll + State Tax + Driver</span>
                        <span className="text-green-600 font-semibold">Included</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-400">
                        <span>Parking Charges</span>
                        <span className="text-red-500 font-semibold">Extra (Customer)</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-extrabold text-gray-800">Total Fare</span>
                        <span className="text-2xl font-extrabold text-[#0F4C81]">₹{roundTripFare.toLocaleString('en-IN')}</span>
                      </div>
                    </div>

                    {/* Payment Option */}
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Payment Option</p>
                      <button
                        onClick={() => setPaymentOption('20percent')}
                        className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-sm transition-all ${paymentOption === '20percent' ? 'border-[#0F4C81] bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}
                      >
                        <div className="text-left">
                          <p className="font-bold text-gray-800">Pay 20% Advance</p>
                          <p className="text-xs text-gray-500">Balance before departure</p>
                        </div>
                        <div className="text-right">
                          <p className="font-extrabold text-[#0F4C81]">₹{advanceAmount.toLocaleString('en-IN')}</p>
                          <p className="text-xs text-gray-400">Now</p>
                        </div>
                      </button>
                      <button
                        onClick={() => setPaymentOption('100percent')}
                        className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-sm transition-all ${paymentOption === '100percent' ? 'border-[#00A86B] bg-green-50' : 'border-gray-200 hover:border-green-200'}`}
                      >
                        <div className="text-left">
                          <p className="font-bold text-gray-800">Pay 100% Online</p>
                          <p className="text-xs text-green-600 font-semibold">✓ Hassle-free</p>
                        </div>
                        <div className="text-right">
                          <p className="font-extrabold text-[#00A86B]">₹{roundTripFare.toLocaleString('en-IN')}</p>
                          <p className="text-xs text-gray-400">Full</p>
                        </div>
                      </button>
                    </div>

                    <button
                      onClick={handleBookNow}
                      className="w-full bg-[#FFB703] hover:bg-yellow-400 text-[#0F4C81] font-extrabold py-3.5 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                    >
                      <Zap size={16} />
                      Book Now – ₹{payableNow.toLocaleString('en-IN')}
                    </button>

                    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
                      <Lock size={11} />
                      <span>Secured by Razorpay • 256-bit SSL</span>
                    </div>
                  </div>
                </div>

                {/* Contact Card */}
                <div className="bg-gradient-to-br from-[#0F4C81] to-[#1a6bb5] rounded-2xl p-4 text-white">
                  <p className="font-bold text-sm mb-1">Need Help Booking?</p>
                  <p className="text-blue-200 text-xs mb-3">Our team is available 24×7</p>
                  <div className="space-y-2">
                    <a
                      href="tel:+918894915859"
                      className="flex items-center justify-center gap-2 bg-white text-[#0F4C81] font-bold text-sm py-2.5 rounded-lg hover:bg-blue-50 transition-colors w-full"
                    >
                      <Phone size={14} />
                      +91 88949 15859
                    </a>
                    <a
                      href="https://wa.me/918894915859?text=Hi%2C%20I%20want%20to%20book%20Manimahesh%20Yatra%20taxi.%20Please%20share%20details."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold text-sm py-2.5 rounded-lg hover:bg-green-500 transition-colors w-full"
                    >
                      <MessageCircle size={14} />
                      WhatsApp Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Booking Form ─────────────────────────────────────────────────── */}
      {showBookingForm && (
        <section id="booking-form-section" className="py-10 bg-white border-t border-gray-100">
          <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-extrabold text-gray-900">Complete Your Booking</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {selectedVehicle.name} • {PICKUP_CITIES.find((c) => c.id === selectedPickup)?.label} → Manimahesh Sahib •{' '}
                  <span className="font-bold text-[#0F4C81]">₹{roundTripFare.toLocaleString('en-IN')}</span>
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-4">
                {/* Payment Error Banner */}
                {paymentError && (
                  <div className="bg-red-50 border border-red-300 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-red-800 text-sm mb-1">Payment Error</p>
                      <p className="text-sm text-red-700">{paymentError}</p>
                      <div className="flex gap-3 mt-3">
                        <button
                          type="button"
                          onClick={() => { setPaymentError(null); setPaymentStatus('idle'); }}
                          className="text-sm font-semibold text-red-700 underline"
                        >
                          Try Again
                        </button>
                        <a
                          href="https://wa.me/918894915859?text=Hi%20Ghumo%20Ji%20Travels%2C%20I%20need%20help%20with%20Manimahesh%20Yatra%20booking."
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                        >
                          <MessageCircle size={13} />
                          Book via WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Enter your full name"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] ${formErrors.name ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number *</label>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData((p) => ({ ...p, mobile: e.target.value }))}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] ${formErrors.mobile ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {formErrors.mobile && <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] ${formErrors.email ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                </div>

                {/* Date + Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Travel Date *</label>
                    <input
                      type="date"
                      value={formData.travelDate}
                      onChange={(e) => setFormData((p) => ({ ...p, travelDate: e.target.value }))}
                      className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] ${formErrors.travelDate ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {formErrors.travelDate && <p className="text-red-500 text-xs mt-1">{formErrors.travelDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Pickup Time *</label>
                    <input
                      type="time"
                      value={formData.travelTime}
                      onChange={(e) => setFormData((p) => ({ ...p, travelTime: e.target.value }))}
                      className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] ${formErrors.travelTime ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {formErrors.travelTime && <p className="text-red-500 text-xs mt-1">{formErrors.travelTime}</p>}
                  </div>
                </div>

                {/* Passengers */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Number of Passengers</label>
                  <select
                    value={formData.passengers}
                    onChange={(e) => setFormData((p) => ({ ...p, passengers: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]"
                  >
                    {Array.from({ length: selectedVehicle.capacity }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} Passenger{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Special Requests (Optional)</label>
                  <textarea
                    value={formData.specialRequests}
                    onChange={(e) => setFormData((p) => ({ ...p, specialRequests: e.target.value }))}
                    placeholder="Any special requirements, pickup address, etc."
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81] resize-none"
                  />
                </div>

                {/* Payment Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Fare</span>
                    <span className="font-bold text-gray-800">₹{roundTripFare.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payment Mode</span>
                    <span className="font-bold text-[#0F4C81]">{paymentOption === '20percent' ? '20% Advance' : '100% Full'}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-blue-200 pt-2">
                    <span className="font-extrabold text-gray-800">Pay Now</span>
                    <span className="font-extrabold text-[#0F4C81] text-lg">₹{payableNow.toLocaleString('en-IN')}</span>
                  </div>
                  {paymentOption === '20percent' && (
                    <p className="text-xs text-gray-500">Balance ₹{pendingAmount.toLocaleString('en-IN')} payable before departure</p>
                  )}
                </div>

                {/* Payment Status */}
                {currentStatus && (
                  <div className={`flex items-center gap-2 p-3 rounded-xl bg-gray-50 border ${paymentStatus === 'failed' ? 'border-red-200 bg-red-50' : 'border-blue-100'}`}>
                    <span className="text-lg">{currentStatus.icon}</span>
                    <span className={`text-sm font-semibold ${currentStatus.color}`}>{currentStatus.text}</span>
                    {(paymentStatus === 'creating-order' || paymentStatus === 'opening-checkout' || paymentStatus === 'verifying') && (
                      <Loader2 size={14} className="animate-spin ml-auto" />
                    )}
                  </div>
                )}

                {/* Pay Button */}
                <button
                  onClick={handlePayment}
                  disabled={isSubmitting}
                  className="w-full bg-[#0F4C81] hover:bg-blue-800 disabled:opacity-60 text-white font-extrabold py-4 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} />
                      Pay ₹{payableNow.toLocaleString('en-IN')} &amp; Confirm Booking
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                  <div className="flex items-center gap-1"><Lock size={11} /><span>SSL Secured</span></div>
                  <div className="flex items-center gap-1"><Shield size={11} /><span>Razorpay</span></div>
                  <div className="flex items-center gap-1"><CheckCircle2 size={11} /><span>Instant Confirmation</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Sticky Mobile Book Now ───────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-2xl px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs text-gray-500">Selected: <span className="font-bold text-gray-800">{selectedVehicle.name}</span></p>
            <p className="text-lg font-extrabold text-[#0F4C81]">₹{roundTripFare.toLocaleString('en-IN')}</p>
          </div>
          <button
            onClick={handleBookNow}
            className="bg-[#FFB703] hover:bg-yellow-400 text-[#0F4C81] font-extrabold px-6 py-3 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 text-sm whitespace-nowrap"
          >
            <Zap size={15} />
            Book Now
          </button>
        </div>
      </div>
      <div className="h-20 lg:hidden" />
    </>
  );
}